import {
  browser, CollectionView, Model, util
} from '@glu/core';
import RowView from '../row/row';
import MessageRowView from '../row/messageRow';
import RowGroupHeaderView from '../row/rowGroupHeader';
import RowGroupHeaderModel from '../row/rowGroupModel';
import RowSubViewWrapper from '../row/rowSubViewWrapper';

import subviewButtonTemplate from './subviewButton.hbs';

/**
 * Add wrapper around cell content for row subview animation
 * @param {jQuery} $cell
 * @returns {jQuery}
 */
const addSubRowWrapper = $cell => {
  $cell.wrapInner('<div />');
  return $cell.children('div');
};

export default CollectionView.extend({
  initialize(opts) {
    this.grid = opts.grid;
    this.gridCid = opts.gridCid;
    this.editOptions = opts.editOptions;
    this.columns = opts.columns;
    this.rowClassName = opts.rowClassName;

    if (opts.groupBy) {
      this.groupByModel = new RowGroupHeaderModel(opts.groupBy);
    }

    this.debouncedGridContentRendered = util.debounce(util.bind(this.gridContentRendered, this), 0);
    this.debouncedHandleChange = util.debounce(util.bind(this.handleChange, this), 5);
  },

  events: {
    'click .btn-grid-expand': 'toggleSubRow'
  },

  delegateEvents(...args) {
    CollectionView.prototype.delegateEvents.apply(this, args);
    this.stopListening(this.collection, 'reset sort sync'); // prevent default CollectionView render on reset
    this.stopListening(this.columns);

    this.listenTo(this.columns, 'change:condition add remove sort sync reorder', this.handleColumnsChange);

    // if we don't have groupBy or rowSubView feature enabled we should
    // use built in Marionette CollectionView handlers for 'add' and 'remove' events
    if (!this.options.groupBy && !this.options.rowSubView) {
      this.listenTo(this.collection, 'reset sort sync', this.debouncedHandleChange);
    } else {
      this.listenTo(this.collection, 'reset add remove sort sync', this.debouncedHandleChange);
    }
  },

  itemView: RowView,

  prepend(view) {
    if (view.el.childNodes.length === 0) {
      view.render();
    }
    if (this.el.firstChild) {
      this.el.insertBefore(view.el, this.el.firstChild);
    } else {
      this.el.appendChild(view.el);
    }
  },

  append(view) {
    if (view.el.childNodes.length === 0) {
      view.render();
    }
    this.el.appendChild(view.el);
  },

  setLastView(view) {
    this.lastView = view;
  },

  setFirstView(view) {
    this.firstView = view;
  },

  onRender() {
    if (this.firstView) {
      this.prepend(this.firstView);
    }

    if (this.lastView) {
      this.append(this.lastView);
    }
    this.onDomRefresh();
  },

  onDomRefresh() {
    this.debouncedGridContentRendered();
  },

  gridContentRendered() {
    if (this.isClosed) {
      return;
    }
    this.grid.trigger('grid:contentRendered');
  },

  getRowSelector() {
    return this.columns.findWhere({
      type: 'rowSelector'
    });
  },

  getColumnCount() {
    const visibleColumns = this.columns.filter(column => {
      if (column.has('condition')) {
        const isFunction = util.isFunction(column.get('condition')) && !column.get('condition')(this.model, column);
        const isBoolean = util.isBoolean(column.get('condition')) && !column.get('condition');
        if (isFunction || isBoolean) {
          return false;
        }
      }
      return true;
    });

    return visibleColumns.length;
  },

  // Internal method to loop through each item in the
  // collection view and show it
  showCollection() {
    const columnCount = this.getColumnCount();

    // Necessary as cannot use :nth-child or :nth-of-type with sub row views
    let isEvenRow = false;

    this.collection.each((item, index) => {
      const RowItemView = this.getItemView(item);

      this.addPreItemView(item, index, columnCount, isEvenRow);

      const view = this.addItemView(item, RowItemView, index);

      // Necessary when using sub-rows, as :nth-child/:nth-of-type isn't "class aware"
      view.el.className += isEvenRow ? ' grid-row grid-row-even' : ' grid-row grid-row-odd';

      this.addPostItemView(item, index, columnCount, view, isEvenRow);

      isEvenRow = !isEvenRow;
    });

    // Reset value ready for next page of results.
    delete this.currentGroupByValue;
  },

  addPreItemView(item, index, columnCount /* , isEvenRow */) {
    this.addRowGroupHeaderView(item, index, columnCount);
  },

  addPostItemView(item, index, columnCount, rowView, isEvenRow) {
    this.addSubRow(item, index, columnCount, rowView, isEvenRow);
    if (item.has('warning') || item.has('error')) {
      this.decorate(item, rowView);
    }
  },

  addSubRow(item, index, columnCount, rowView, isEvenRow) {
    if (!this.options.rowSubView) {
      return;
    }

    const RowSubView = this.options.rowSubView(item);
    const id = util.uniqueId();

    if (!RowSubView) {
      return;
    }

    rowView.$('td:first').prepend(subviewButtonTemplate({
      id,
      disabled: !RowSubView
    }));

    if (!RowSubView) {
      rowView.$('.btn-grid-expand').attr('disabled', true);
      return;
    }

    const subRowModel = item.clone();

    subRowModel.set({
      columnCount
    });

    const view = this.addItemView(subRowModel, RowSubViewWrapper, index);

    view.el.className += isEvenRow ? ' grid-subrow grid-row-even' : ' grid-subrow grid-row-odd';
    view.$el.attr('id', `grid-subview-${id}`); // for aria-controls association with button
    rowView.$('.btn-grid-expand').data('viewCid', view.cid); // for aria-controls association with button
  },

  toggleSubRow(e) {
    const $el = this.$(e.currentTarget);
    const viewCid = $el.data('viewCid');
    const $nextRow = $el.closest('tr').next();
    const view = this.children.findByCid(viewCid);
    let $wrapper;

    // Fail fast
    if (!$nextRow.hasClass('grid-subrow')) {
      return;
    }

    const $subCell = $nextRow.children().first();
    $wrapper = $subCell.children('div');

    // Wrapper is needed for the animation of table cell contents
    if ($wrapper.length === 0) {
      $wrapper = addSubRowWrapper($subCell);
    }

    const rowIsHidden = $nextRow.hasClass('hidden');

    if (rowIsHidden) {
      // Turn off the hidden class & add
      // the zero-padding top/bottom "hidden-subrow-animate"
      $nextRow
        .removeClass('hidden')
        .addClass('hidden-subrow-animate');

      // Hide the data in the cells
      $wrapper.hide();

      // Remove the hidden-subrow-animate class to start the CSS transition
      // Also start the .slideDown()
      $nextRow.removeClass('hidden-subrow-animate');
      $wrapper.slideDown(500);

      if (view) {
        view.triggerMethod('subRowExpanded');
      }
    } else {
      $nextRow.addClass('hidden-subrow-animate');
      $wrapper.slideUp(500, () => {
        $nextRow.addClass('hidden').removeClass('hidden-subrow-animate');
      });
    }

    // Looks backwards, but if rowIsHidden *was* true above, expanded is now true
    $el.attr('aria-expanded', rowIsHidden);
  },

  addRowGroupHeaderView(item, index, columnCount) {
    if (!this.options.groupBy) {
      return;
    }

    const groupByValue = item.get(this.options.groupBy.field);
    const groupByTitle = item.get(this.options.groupBy.titleField);
    const hasRowSelector = this.getRowSelector();

    if (this.currentGroupByValue !== groupByValue) {
      this.groupByModel = new RowGroupHeaderModel(util.extend({
        currentValue: groupByValue,
        currentTitle: groupByTitle,
        columnCount,
        hasRowSelector
      }, this.options.groupBy));
      this.groupId = this.groupByModel.cid;

      this.addItemView(this.groupByModel, RowGroupHeaderView, index);
      this.currentGroupByValue = groupByValue;
    }

    // Need to set 'groupId' property
    // silent is for prevent re-rendering
    item.set('groupId', this.groupId, {
      silent: true
    });
  },

  decorate(item, view) {
    let decorModel;

    if (item.has('error')) {
      decorModel = this.mapDecorModel(item, 'error');
    } else if (item.has('warning')) {
      decorModel = this.mapDecorModel(item, 'warning');
    }

    const decorView = this.createDecorView(decorModel, view.$('td').length);

    decorView.render();

    this.attachView(decorView, view);

    view.$el.addClass(`${decorModel.get('type')}-content`);

    decorView.$el.addClass(`${decorModel.get('type')}-messages`);

    this.children.add(decorView);
  },

  attachView(rowView, view) {
    if (view.el.nextSibling) {
      view.el.parentNode.insertBefore(rowView.el, view.el.nextSibling);
    } else {
      view.el.parentNode.appendChild(rowView.el);
    }
  },

  createDecorView(model, colspan) {
    const view = new MessageRowView({
      model,
      colspan
    });

    return view;
  },

  /**
   * Converts a warning or error message into a standardized
   * model for easier template consumption.
   */
  mapDecorModel(model, type) {
    if (!model.has(type)) {
      return [];
    }
    const ret = {
      messages: []
    };
    const message = model.get(type);

    // below code is suggested by eslint role "guard-for-in".
    for (const key in message) {
      if (message.hasOwnProperty(key)) {
        ret.type = type;
        ret.messages = ret.messages.concat(message[key]);
        ret.field = key;
      }
    }
    return new Model(ret);
  },

  // TODO leave this method for backwards compatibility - remove in Glu 1.9
  setCollection(collection) {
    this.collection = collection;

    // if we don't have groupBy or rowSubView feature enabled
    // we should use built in Marionette CollectionView collection events handling.
    if (!this.options.groupBy && !this.options.rowSubView) {
      if (this.collection) {
        this.stopListening(this.collection, 'reset');
      }
      // We debounce this handler, because otherwise
      // it is called as many times as there are models in the collection.
      this.listenTo(this.collection, 'reset sort sync', this.debouncedHandleChange);
      return;
    }

    if (this.collection) {
      this.stopListening(this.collection);
    }

    // We debounce this handler, because otherwise
    // it is called as many times as there are models in the collection.
    this.listenTo(this.collection, 'reset add remove sort sync', this.debouncedHandleChange);
  },

  handleColumnsChange() {
    // this view will be re-rendered for IE in the tableLayout class
    if (browser.msie) {
      return false;
    }

    this.handleChange();
    return undefined;
  },

  handleChange(e) {
    if (this.isClosed) {
      return undefined;
    }

    this.renderAndRestoreFocus();
    return e;
  },

  itemViewOptions(item) {
    return {
      columns: this.columns,
      rowClassName: this.rowClassName,
      editOptions: this.editOptions,
      gridCid: this.gridCid,
      grid: this.grid,
      RowSubView: util.isFunction(this.options.rowSubView) ?
        this.options.rowSubView(item) : undefined,
      rowSubViewOptions: this.options.rowSubViewOptions,
      disableDynamicColumnWidths: this.options.disableDynamicColumnWidths,
      theme: this.options.theme
    };
  },

  onClose() {
    if (this.firstView) {
      this.firstView.close();
    }
    if (this.lastView) {
      this.lastView.close();
    }
  }
});
